import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

import { Box, Paper, TableContainer, Table, TableBody, TableRow, TablePagination, Skeleton } from '@mui/material';
import domainData from 'store/domainStore';
import CustomTableHead from './customTableHead';
import CustomTableCell from './customTableCell';
import TablePaginationActions from './customPaginationAction';
import classes from './styles';

function CustomTable({
  rowUniqueKey = 'id',
  loading = false,
  tableSources = { data: [], count: 0 },
  tableOptions,
  getDataOnChange = null,
  filteredParams,
  setFilteredParams = null,
  selectedRows = [],
  setSelectedRows = null,
  handleShowQR = null,
  handleShowInMarket = null,
  handleRemoveAction = null,
  handleResponseAction = null,
  withEditAction = true,
  withQRAction = false,
  withInMarketAction = false,
  withRemoveAction = false,
  removeActionCondition = null,
  withResponseAction = false,
  withCheckbox = true,
  withOpenAction = true,
  withoutPagination = false,
  withFilterBar = true,
  withCloneAction = false,
  cloneActionCondition = null,
  inMarketActionCondition = null,
  QRActionCondition = null,
  handleCloneAction = null,
  openPath = null,
  toolbarView = null,
  disableCondition = null,
  stickyHeader = false,
  /** Optional content rendered next to the “No results found” row (e.g. action buttons). */
  emptyRowSlot = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { limit, skip } = filteredParams?.params ?? {};
  const sortObj = filteredParams?.params?.sort || {};
  const { order, field: orderBy } = sortObj;

  const [rowsPerPage, setRowsPerPage] = useState(parseInt(limit, 10));

  const { fields, rowsPerPageOptions } = tableOptions;
  const { data: tableData, count } = tableSources;

  useEffect(() => {
    setRowsPerPage(parseInt(limit, 10));
  }, [limit]);

  const page = useMemo(() => parseInt(skip / limit, 10), [skip, limit]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newFilter = {
      params: {
        ...filteredParams.params,
        sort: { field: property, order: isAsc ? 'desc' : 'asc' },
        skip: 0,
      },
    };
    setFilteredParams(newFilter);
    getDataOnChange(newFilter);
  };

  const handleAllSelected = event => {
    if (event.target.checked) {
      const newSelected = tableData.map(n => n[rowUniqueKey]);
      const result = [];
      if (setSelectedRows) {
        newSelected.forEach(item => {
          if (!selectedRows.includes(item)) {
            result.push(item);
          }
        });
        setSelectedRows([...selectedRows, ...result]);
      }
      return;
    }

    const newSelected = [];
    for (let i = 0; i < selectedRows.length; i += 1) {
      if (!tableData.find(row => row[rowUniqueKey] === selectedRows[i])) {
        newSelected.push(selectedRows[i]);
      }
    }
    setSelectedRows(newSelected);
  };

  const handleClick = row => {
    if (withCheckbox) {
      const name = row[rowUniqueKey];
      const selectedIndex = selectedRows.indexOf(name);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1));
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
      }
      setSelectedRows(newSelected);
    }
  };

  const onEdit = row => {
    navigate(`${location.pathname}/${row.id}`);
  };

  const onShowQR = row => {
    if (handleShowQR && typeof handleShowQR === 'function') {
      handleShowQR(row.id);
    }
  };

  const onShowInMarket = row => {
    if (handleShowInMarket && typeof handleShowInMarket === 'function') {
      handleShowInMarket(row.id);
    }
  };

  const onRemove = row => {
    if (handleRemoveAction && typeof handleRemoveAction === 'function') {
      handleRemoveAction(row.id);
    }
  };

  const onResponse = row => {
    if (handleResponseAction && typeof handleResponseAction === 'function') {
      handleResponseAction(row);
    }
  };

  const tableSelectsCount = () => {
    let tableSelectedCount = 0;
    tableData?.forEach(row => {
      if (selectedRows.includes(row[rowUniqueKey])) {
        tableSelectedCount += 1;
      }
    });
    return tableSelectedCount;
  };

  const handleChangePage = (event, newPage) => {
    const newFilter = {
      params: {
        ...filteredParams.params,
        skip: newPage * filteredParams.params.limit,
      },
    };
    setFilteredParams(newFilter);
    getDataOnChange(newFilter);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    const newFilter = {
      params: {
        ...filteredParams.params,
        skip: 0,
        limit: parseInt(event.target.value, 10),
      },
    };
    setFilteredParams(newFilter);
    getDataOnChange(newFilter);
  };

  const isSelected = name => selectedRows.indexOf(name) !== -1;

  const collectSkeletonContainer = fields.map(fld => (
    <Skeleton key={fld.id} sx={classes.skeletonItem} animation="pulse" />
  ));

  const isUserSuperAdmin = domainData.userIsSuperAdmin();

  const currentSp = domainData.getCurrentSP();

  return (
    <Box>
      <Paper
        elevation={1}
        sx={{
          ...classes.paperWithShadow,
          ...(stickyHeader ? classes.stickyHeaderPaper : null),
        }}>
        {withFilterBar && toolbarView}
        <TableContainer>
          <Table stickyHeader={!!stickyHeader} sx={stickyHeader ? {} : classes.table} size="small" padding="checkbox">
            {!loading && (
              <CustomTableHead
                tableSelects={tableSelectsCount()}
                numSelected={selectedRows.length}
                sortObj={sortObj}
                handleAllSelected={handleAllSelected}
                onRequestSort={tableData?.length > 0 ? handleRequestSort : null}
                rowCount={tableData?.length}
                fields={fields}
                filteredParams={filteredParams}
                withEditAction={!!tableData?.length && withEditAction}
                withQRAction={!!tableData?.length && withQRAction}
                withInMarketAction={!!tableData?.length && withInMarketAction}
                withCheckbox={!!tableData?.length && withCheckbox}
                withOpenAction={!!tableData?.length && withOpenAction}
                withCloneAction={!!tableData?.length && withCloneAction}
                withRemoveAction={!!tableData?.length && withRemoveAction}
                withResponseAction={!!tableData?.length && withResponseAction}
                stickyHeader={stickyHeader}
              />
            )}
            <TableBody sx={loading ? classes.tableBody : {}}>
              {tableData?.map(row => {
                const isItemSelected = isSelected(row[rowUniqueKey]);
                return loading ? (
                  <tr key={row[rowUniqueKey]} style={classes.skeletonContainer}>
                    <td>{collectSkeletonContainer}</td>
                  </tr>
                ) : (
                  <TableRow
                    key={row[rowUniqueKey]}
                    sx={{
                      ...classes.tableRow,
                      ...(row.isApproved === false && classes.rowError),
                      ...(disableCondition && disableCondition(row) && classes.rowDisabled),
                      ...(row.isDraft && classes.rowDraft),
                    }}>
                    {withCheckbox && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        handleClickIcon={handleClick}
                        data={row}
                        key={`${row[rowUniqueKey]}-checkbox`}
                        isItemSelected={isItemSelected}
                        isCheckbox={withCheckbox}
                        filteredParams={filteredParams}
                      />
                    )}
                    {fields.map(
                      item =>
                        (!item.show || item.show({ currentSp, isUserSuperAdmin })) && (
                          <CustomTableCell
                            stickyHeader={stickyHeader}
                            key={item.id}
                            cellItem={item}
                            data={row}
                            filteredParams={filteredParams}
                          />
                        ),
                    )}
                    {withCloneAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        isShow={cloneActionCondition}
                        handleClickIcon={handleCloneAction}
                        data={row}
                        key={`${row[rowUniqueKey]}-clone`}
                        isCloneAction
                        filteredParams={filteredParams}
                      />
                    )}
                    {withInMarketAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        isShow={inMarketActionCondition}
                        handleClickIcon={onShowInMarket}
                        data={row}
                        key={`${row[rowUniqueKey]}-inMarket`}
                        isInMarketAction
                      />
                    )}
                    {withQRAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        isShow={QRActionCondition}
                        handleClickIcon={onShowQR}
                        data={row}
                        key={`${row[rowUniqueKey]}-qr`}
                        isQRAction
                      />
                    )}
                    {withEditAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        handleClickIcon={onEdit}
                        data={row}
                        key={`${row[rowUniqueKey]}-edit`}
                        isEditAction
                      />
                    )}
                    {withOpenAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        openPath={openPath ? openPath(row) : `${location.pathname}/${row.id}/show`}
                        data={row}
                        key={`${row[rowUniqueKey]}-show`}
                        isOpenAction
                      />
                    )}
                    {withRemoveAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        isShow={removeActionCondition}
                        handleClickIcon={onRemove}
                        data={row}
                        key={`${row[rowUniqueKey]}-remove`}
                        isRemoveAction
                      />
                    )}
                    {withResponseAction && (
                      <CustomTableCell
                        stickyHeader={stickyHeader}
                        handleClickIcon={onResponse}
                        data={row}
                        key={`${row[rowUniqueKey]}-response`}
                        isResponseAction
                      />
                    )}
                  </TableRow>
                );
              })}
              {!tableData?.length && (
                <TableRow style={{ height: '60px' }}>
                  <CustomTableCell
                    stickyHeader={stickyHeader}
                    isEmptyCell
                    loading={loading}
                    emptyRowSlot={emptyRowSlot}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!withoutPagination && !loading && tableData?.length > 0 && (
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={props => TablePaginationActions({ ...props })}
            classes={{ toolbar: classes.paginationRoot }}
          />
        )}
      </Paper>
    </Box>
  );
}

CustomTable.propTypes = {
  rowUniqueKey: PropTypes.string,
  loading: PropTypes.bool,
  tableSources: PropTypes.shape({
    data: PropTypes.instanceOf(Array),
    count: PropTypes.number,
  }),
  tableOptions: PropTypes.shape({
    fields: PropTypes.instanceOf(Array),
    rowsPerPageOptions: PropTypes.instanceOf(Array),
    searchFields: PropTypes.instanceOf(Array),
  }).isRequired,
  getDataOnChange: PropTypes.func,
  filteredParams: PropTypes.instanceOf(Object).isRequired,
  setFilteredParams: PropTypes.func,
  selectedRows: PropTypes.instanceOf(Array),
  setSelectedRows: PropTypes.func,
  handleShowQR: PropTypes.func,
  handleShowInMarket: PropTypes.func,
  handleRemoveAction: PropTypes.func,
  withEditAction: PropTypes.bool,
  withQRAction: PropTypes.bool,
  withInMarketAction: PropTypes.bool,
  withRemoveAction: PropTypes.bool,
  removeActionCondition: PropTypes.bool,
  withCheckbox: PropTypes.bool,
  withOpenAction: PropTypes.bool,
  withoutPagination: PropTypes.bool,
  withFilterBar: PropTypes.bool,
  withCloneAction: PropTypes.bool,
  handleResponseAction: PropTypes.func,
  withResponseAction: PropTypes.bool,
  cloneActionCondition: PropTypes.func,
  inMarketActionCondition: PropTypes.func,
  QRActionCondition: PropTypes.func,
  handleCloneAction: PropTypes.func,
  openPath: PropTypes.func,
  toolbarView: PropTypes.node,
  disableCondition: PropTypes.func,
  stickyHeader: PropTypes.bool,
  emptyRowSlot: PropTypes.node,
};

export default CustomTable;
